import { ApolloError } from '@apollo/client';
import styled from '@emotion/styled';
import { ArrowLeft } from '@phosphor-icons/react';
import {
  Box,
  GridLayout,
  GridLayoutColumn,
  IconButton,
  SpaceKey,
  SpinnerFullPage,
  Text,
  Theme,
} from '@updater/ui-uds';
import { MovingAppBrandedHeader } from 'components/branded-header';
import { LeftAligned } from 'components/left-aligned';
import { useRouter } from 'next/router';
import React from 'react';
import { useTracking } from 'react-tracking';
import { ResponsiveValue } from 'styled-system';

export interface ContentProps {
  /**
   * As of now, different flows have different maxWidths
   * Instead of copy pasting the same code and changing the maxWidth (there is still one copy-paste in moving-services flow)
   * we can use this component with a dynamic maxWidth.
   * I wish we had more clarity from the design though, so we made something more typed or constraint.
   */
  children?: React.ReactNode;
  title?: string;
  subtitle?: string | React.ReactNode;
  maxWidth?: number | null;
  margin?: number | string | null;
  paddingBottom?: false | ResponsiveValue<'auto' | SpaceKey, Theme>;
  paddingTop?: ResponsiveValue<'auto' | SpaceKey, Theme>;
  loading?: boolean;
  includeBackButton?: boolean;
  /**
   * Only shows when title and loading are true.
   */
  backButtonPath?: string | undefined;
  error?: ApolloError | string | undefined;
  hasFormContainer?: boolean | undefined;
  fullHeight?: boolean;
}

interface BackgroundProps {
  backgroundColor?: string | undefined;
}

export interface ProfileTemplateProps extends ContentProps, BackgroundProps {
  hasHeaderMenu?: boolean;
}

const BodyWrapper = styled.div`
  background-color: rgba(245, 245, 245, 0.95);
  border-radius: ${({ theme }) => theme.radii.xs};
  padding: 34px 16px;
  ${({ theme }) => theme.mediaQueries.l} {
    padding: 34px 130px 100px 34px;
  }
`;

const ContentBody = styled.div<ContentProps>`
  margin: ${({ margin }) => (margin ? `${margin}` : 0)};
  max-width: ${({ maxWidth }) => (maxWidth ? `${maxWidth}px` : null)};
  padding-bottom: ${({ theme, paddingBottom }) =>
    paddingBottom !== false && theme.space.xxxxl}px;
  position: relative;
  //prop needed to center tvi loader in full page
  height: ${({ fullHeight }) => fullHeight && '100vh'};
`;

const Background = styled.div<BackgroundProps>`
  background-color: ${({ backgroundColor }) => backgroundColor};
`;

export const Content: React.FC<ContentProps> = ({
  children,
  title,
  subtitle,
  maxWidth,
  loading,
  includeBackButton,
  backButtonPath,
  error,
  hasFormContainer,
  margin = undefined,
  paddingBottom: pb = 'xs',
  paddingTop: pt = ['s', 's', 'xxl'],
  fullHeight,
}) => {
  const router = useRouter();
  const { trackEvent } = useTracking();

  const renderContentBody = () => {
    if (hasFormContainer) {
      return (
        <ContentBody maxWidth={maxWidth} margin={margin} paddingBottom={pb}>
          <BodyWrapper>{children}</BodyWrapper>
        </ContentBody>
      );
    }
    return (
      <ContentBody
        maxWidth={maxWidth}
        margin={margin}
        fullHeight={fullHeight}
        paddingBottom={pb}
      >
        {children}
      </ContentBody>
    );
  };

  const handleBackButton = () => {
    trackEvent({
      object: 'backButton',
      verb: 'clicked',
    });
    if (backButtonPath) {
      router.push(backButtonPath);
    } else {
      router.back();
    }
  };

  return (
    <LeftAligned p="m" paddingTop={pt}>
      {!loading && !error && title ? (
        <GridLayout includeMargin={false}>
          <GridLayoutColumn span={['4', null, '3', '6']}>
            <Box pb={pb ? pb : undefined}>
              {includeBackButton && (
                <IconButton
                  variant="secondary"
                  size="s"
                  margin="0"
                  icon={<ArrowLeft />}
                  onClick={() => handleBackButton()}
                  dataCy="back-button"
                  data-testid="back-button"
                />
              )}
              <Text
                as="h1"
                variant={['xxl', 'xxl', 'xxl', 'xxxl']}
                pt={includeBackButton ? 'm' : undefined}
                data-cy="page-title"
              >
                {title}
              </Text>
              {subtitle && (
                <Text
                  as="h2"
                  mt="xs"
                  variant="m"
                  data-cy="page-subtitle"
                  style={{ whiteSpace: 'pre-line' }}
                >
                  {subtitle}
                </Text>
              )}
            </Box>
          </GridLayoutColumn>
        </GridLayout>
      ) : (
        <></>
      )}
      {loading && !error ? <SpinnerFullPage /> : <></>}
      {error ? (
        <Box pb="xs">
          {includeBackButton && (
            <IconButton
              variant="secondary"
              size="s"
              margin="0"
              icon={<ArrowLeft />}
              onClick={() => handleBackButton()}
              dataCy="back-button"
              data-testid="back-button"
            />
          )}
          <Text
            as="h1"
            variant={['xxl', 'xxl', 'xxl', 'xxxl']}
            pt={includeBackButton ? 'm' : undefined}
            data-cy="page-title"
          >
            {error.toString()}
          </Text>
        </Box>
      ) : (
        <></>
      )}
      {!loading && !error ? renderContentBody() : <></>}
    </LeftAligned>
  );
};

export const PageTemplate: React.FC<ProfileTemplateProps> = ({
  children,
  title = '',
  subtitle = '',
  maxWidth = 387,
  loading = false,
  includeBackButton = true,
  backButtonPath,
  hasHeaderMenu = true,
  error,
  backgroundColor,
  hasFormContainer,
  margin,
  paddingBottom,
  paddingTop,
  fullHeight = false,
}) => {
  return (
    <>
      <MovingAppBrandedHeader />
      {backgroundColor ? (
        <Background backgroundColor={backgroundColor}>
          <Content
            title={title}
            subtitle={subtitle}
            maxWidth={maxWidth}
            loading={loading}
            includeBackButton={includeBackButton}
            backButtonPath={backButtonPath}
            error={error}
            hasFormContainer={hasFormContainer}
            margin={margin}
            paddingBottom={paddingBottom}
            paddingTop={paddingTop}
          >
            {children}
          </Content>
        </Background>
      ) : (
        <Content
          title={title}
          subtitle={subtitle}
          maxWidth={maxWidth}
          loading={loading}
          includeBackButton={includeBackButton}
          backButtonPath={backButtonPath}
          error={error}
          hasFormContainer={hasFormContainer}
          margin={margin}
          paddingBottom={paddingBottom}
          paddingTop={paddingTop}
          fullHeight={fullHeight}
        >
          {children}
        </Content>
      )}
    </>
  );
};

export default PageTemplate;
